import React, { useState, useEffect, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import UserAvatar from './UserAvatar';
import { Coins, Clock, History, FileText, TrendingDown, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface UserProfileSectionProps {
  user: User | null;
  coins: number;
}

const UserProfileSection: React.FC<UserProfileSectionProps> = ({ user, coins }) => {
  const [lastRecharge, setLastRecharge] = useState<string>('--/--/----');
  const [spentThisMonth, setSpentThisMonth] = useState<number>(0);
  const [cvsGenerated, setCvsGenerated] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  // Usar useCallback para evitar re-crear la función en cada render
  const loadTransactionData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      console.log("Cargando datos de transacciones para el usuario:", user.id);

      // Obtener la última recarga (compra)
      const { data: recentTx, error: txError } = await supabase
        .from('coin_transactions')
        .select('created_at')
        .eq('user_id', user.id)
        .gt('amount', 0)
        .eq('type', 'purchase')
        .order('created_at', { ascending: false })
        .limit(1);

      console.log("Resultado de última recarga:", recentTx, txError);

      if (!txError && recentTx && recentTx.length > 0) {
        const txDate = new Date(recentTx[0].created_at);
        setLastRecharge(txDate.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }));
      } else {
        setLastRecharge('No hay recargas');
      }

      // Calcular gasto del mes (créditos negativos)
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const { data: spentData, error: spentError } = await supabase
        .from('coin_transactions')
        .select('amount')
        .eq('user_id', user.id)
        .lt('amount', 0)
        .eq('type', 'credit')
        .gte('created_at', firstDayOfMonth.toISOString());

      console.log("Resultado de gastos mensuales:", spentData, spentError);

      if (!spentError && spentData) {
        const totalSpent = spentData.reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
        setSpentThisMonth(totalSpent);
      }

      // Contar CVs generados (transacciones con créditos negativos y descripción CV)
      const { data: usageData, error: usageError } = await supabase
        .from('coin_transactions')
        .select('id, description')
        .eq('user_id', user.id)
        .eq('type', 'credit')
        .ilike('description', '%CV%');

      console.log("Resultado de CVs generados:", usageData, usageError);

      if (!usageError && usageData) {
        setCvsGenerated(usageData.length);
      }
    } catch (err) {
      console.error('Error al obtener datos de transacciones:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadTransactionData();
    }
  }, [user, loadTransactionData]);

  // ---- NUEVO useEffect para escuchar actualizaciones ----
  useEffect(() => {
    const handleCoinsUpdate = () => {
      console.log('[UserProfileSection] Evento coinsUpdated recibido, recargando datos...');
      loadTransactionData();
    };

    window.addEventListener('coinsUpdated', handleCoinsUpdate);

    // Limpiar el listener al desmontar
    return () => {
      window.removeEventListener('coinsUpdated', handleCoinsUpdate);
    };
  }, [loadTransactionData]); // <-- Depender de loadTransactionData
  // ---- FIN NUEVO useEffect ----

  const getUserDisplayName = () => {
    if (!user) return '';
    return user.user_metadata?.full_name ||
      user.email?.split('@')[0] ||
      'Usuario';
  };

  // Función para formatear números con separador de miles utilizando puntos
  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  return (
    <div className="bg-gradient-to-br from-blue-900/40 via-indigo-900/40 to-purple-900/40 rounded-xl p-4 border border-purple-500/20 shadow-lg overflow-hidden relative">
      {/* Efectos de fondo */}
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"></div>
      <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-blue-500/10 rounded-full blur-xl"></div>

      {/* Contenido estructurado */}
      <div className="flex flex-col lg:flex-row items-center justify-between gap-3 relative z-10">
        {/* Avatar y nombre juntos en modo web, mejor centrados */}
        <div className="flex flex-col lg:flex-row items-center lg:items-center gap-3 lg:pr-6 lg:border-r lg:border-purple-500/20">
          {/* Avatar del usuario */}
          <div className="flex-shrink-0">
            <UserAvatar user={user} size="md" className="border-2 border-purple-500/50" />
          </div>

          {/* Nombre e información */}
          <div className="flex flex-col items-center lg:items-start my-auto">
            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200 mb-1 text-center lg:text-left">
              ¡Bienvenido, {getUserDisplayName()}!
            </h2>
            <p className="text-sm text-purple-100/80 mb-0 text-center lg:text-left">
              Recarga monedas para optimizar tus CVs
            </p>
          </div>
        </div>

        {/* Balance de monedas - Rediseño con cuadrantes e iconos */}
        <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl p-4 border border-purple-500/30 w-full lg:w-auto lg:flex-1 mt-2 lg:mt-0">
          <div className="flex items-center justify-between mb-4">
            <div className="text-yellow-500 font-medium flex items-center gap-1.5">
              <Coins className="w-4 h-4" />
              <span>Balance Actual</span>
            </div>
            <Link
              to="/transactions"
              className="flex items-center text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
              <History className="w-3 h-3 mr-1" />
              <span>Historial</span>
            </Link>
          </div>

          {/* Mostrar balance principal */}
          <div className="flex items-center justify-center mb-5">
            <div className="text-center">
              <div className="text-yellow-400 text-4xl font-bold mb-1">
                {formatNumber(coins)}
              </div>
              <div className="text-yellow-300/70 text-sm">Monedas</div>
            </div>
          </div>

          {/* Cuadrantes de métricas con iconos */}
          <div className="grid grid-cols-2 gap-3">
            {/* Cuadrante 1: Última recarga */}
            <div className="bg-gray-800/70 rounded-lg p-3 flex flex-col items-center text-center">
              <Clock className="w-5 h-5 text-blue-400 mb-2" />
              <div className="text-xs text-gray-400 mb-1">Última Recarga</div>
              <div className="text-sm font-medium text-white">
                {loading ? '...' : lastRecharge}
              </div>
            </div>

            {/* Cuadrante 2: Gastado este mes */}
            <div className="bg-gray-800/70 rounded-lg p-3 flex flex-col items-center text-center">
              <TrendingDown className="w-5 h-5 text-red-400 mb-2" />
              <div className="text-xs text-gray-400 mb-1">Gastado este mes</div>
              <div className="text-sm font-medium text-white">
                {loading ? '...' : `${formatNumber(spentThisMonth)}`}
              </div>
            </div>

            {/* Cuadrante 3: CVs generados */}
            <div className="bg-gray-800/70 rounded-lg p-3 flex flex-col items-center text-center">
              <FileText className="w-5 h-5 text-purple-400 mb-2" />
              <div className="text-xs text-gray-400 mb-1">CVs generados</div>
              <div className="text-sm font-medium text-white">
                {loading ? '...' : formatNumber(cvsGenerated)}
              </div>
            </div>

            {/* Cuadrante 4: CVs disponibles */}
            <div className="bg-gray-800/70 rounded-lg p-3 flex flex-col items-center text-center">
              <Calendar className="w-5 h-5 text-green-400 mb-2" />
              <div className="text-xs text-gray-400 mb-1">CVs disponibles</div>
              <div className="text-sm font-medium text-white">
                {loading ? '...' : formatNumber((coins))}
              </div>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-gray-700/50 text-center">
            <div className="text-xs text-gray-400">
              Cada generación de CV cuesta <span className="text-yellow-400 font-medium">1</span> moneda
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileSection; 